// Text-quote anchoring for highlights and notes: the quote plus surrounding context (W3C
// TextQuoteSelector), never DOM offsets, so marks survive re-renders and light edits. Re-anchoring
// picks the occurrence whose context matches best.

export interface QuoteSelector {
  quote: string;
  prefix: string;
  suffix: string;
}

interface Mapped {
  text: string;
  nodes: { node: Text; start: number }[];
}

const CONTEXT = 32;

function textNodes(root: Node): Text[] {
  const out: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = (node as Text).parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest('script, style, .lb-board, .lb-toolbar, .lb-popover')) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let n: Node | null;
  while ((n = walker.nextNode())) out.push(n as Text);
  return out;
}

function mapText(root: Node): Mapped {
  let text = '';
  const nodes: { node: Text; start: number }[] = [];
  for (const node of textNodes(root)) {
    nodes.push({ node, start: text.length });
    text += node.data;
  }
  return { text, nodes };
}

function firstTextIn(node: Node): Text | null {
  if (node.nodeType === Node.TEXT_NODE) return node as Text;
  const list = textNodes(node);
  return list.length ? list[0] : null;
}

function lastTextIn(node: Node): Text | null {
  if (node.nodeType === Node.TEXT_NODE) return node as Text;
  const list = textNodes(node);
  return list.length ? list[list.length - 1] : null;
}

function globalOffset(m: Mapped, container: Node, offset: number): number {
  if (container.nodeType === Node.TEXT_NODE) {
    const entry = m.nodes.find((e) => e.node === container);
    return entry ? entry.start + offset : -1;
  }
  const children = container.childNodes;
  if (offset < children.length) {
    // Boundary before children[offset]: the start of the first text node inside it (or after it).
    for (let i = offset; i < children.length; i++) {
      const t = firstTextIn(children[i]);
      if (t) {
        const entry = m.nodes.find((e) => e.node === t);
        if (entry) return entry.start;
      }
    }
    return m.text.length;
  }
  const t = lastTextIn(container);
  if (!t) return -1;
  const entry = m.nodes.find((e) => e.node === t);
  return entry ? entry.start + t.data.length : -1;
}

export function describeRange(root: HTMLElement, range: Range): QuoteSelector | null {
  const m = mapText(root);
  const s = globalOffset(m, range.startContainer, range.startOffset);
  const e = globalOffset(m, range.endContainer, range.endOffset);
  if (s < 0 || e < 0 || e <= s) return null;
  const quote = m.text.slice(s, e);
  if (!quote.trim()) return null;
  return {
    quote,
    prefix: m.text.slice(Math.max(0, s - CONTEXT), s),
    suffix: m.text.slice(e, e + CONTEXT),
  };
}

function positionToNode(m: Mapped, pos: number, preferEnd: boolean): { node: Text; offset: number } | null {
  for (let i = 0; i < m.nodes.length; i++) {
    const entry = m.nodes[i];
    const end = entry.start + entry.node.data.length;
    if (pos < end || (preferEnd && pos === end) || (i === m.nodes.length - 1 && pos <= end)) {
      if (pos >= entry.start) return { node: entry.node, offset: pos - entry.start };
    }
  }
  return null;
}

function overlap(a: string, b: string, fromEnd: boolean): number {
  // How many characters of context agree, counted from the boundary outwards.
  let n = 0;
  const max = Math.min(a.length, b.length);
  for (let i = 0; i < max; i++) {
    const ca = fromEnd ? a[a.length - 1 - i] : a[i];
    const cb = fromEnd ? b[b.length - 1 - i] : b[i];
    if (ca !== cb) break;
    n++;
  }
  return n;
}

export function findRange(root: HTMLElement, sel: QuoteSelector): Range | null {
  if (!sel.quote) return null;
  const m = mapText(root);
  const candidates: number[] = [];
  let idx = m.text.indexOf(sel.quote);
  while (idx >= 0 && candidates.length < 50) {
    candidates.push(idx);
    idx = m.text.indexOf(sel.quote, idx + 1);
  }
  if (candidates.length === 0) return null;
  let best = candidates[0];
  let bestScore = -1;
  for (const c of candidates) {
    const before = m.text.slice(Math.max(0, c - CONTEXT), c);
    const after = m.text.slice(c + sel.quote.length, c + sel.quote.length + CONTEXT);
    const score = overlap(before, sel.prefix || '', true) + overlap(after, sel.suffix || '', false);
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  const start = positionToNode(m, best, false);
  const end = positionToNode(m, best + sel.quote.length, true);
  if (!start || !end) return null;
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
}

// Wrap every text node the range touches in a <mark>, splitting the boundary nodes. Returns the
// marks so callers can attach handlers.
export function highlightRange(range: Range, className: string, data: Record<string, string>): HTMLElement[] {
  const marks: HTMLElement[] = [];
  const root = range.commonAncestorContainer;
  const nodes = textNodes(root.nodeType === Node.TEXT_NODE ? root.parentNode! : root).filter((t) =>
    range.intersectsNode(t)
  );
  for (const original of nodes) {
    let node = original;
    if (node === range.endContainer && range.endOffset < node.data.length) {
      node.splitText(range.endOffset);
    }
    if (node === range.startContainer && range.startOffset > 0) {
      node = node.splitText(range.startOffset);
    }
    if (!node.data.length) continue;
    const mark = document.createElement('mark');
    mark.className = className;
    for (const [k, v] of Object.entries(data)) mark.dataset[k] = v;
    node.parentNode!.insertBefore(mark, node);
    mark.appendChild(node);
    marks.push(mark);
  }
  return marks;
}

export function removeMarks(annotationId: number): void {
  document.querySelectorAll<HTMLElement>(`mark[data-ann="${annotationId}"]`).forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
}
